import moment from "../plugins/moment";
import RemainingDate from "../models/RemainingDate";
import { WeekRange } from "../models/PayRun";
import AppHelper from "./AppHelper";

export default class Utils {
  public static isArrayEmptyOrNull<T>(arr: T[] | null | undefined): boolean {
    return !arr || arr.length === 0;
  }
  public static formatTwoDecimal(number: number): number {
    const formatted = Math.trunc(number * 100) / 100;
    return formatted;
  }
  public static getWeekDays() {
    return [1, 2, 3, 4, 5, 6, 0];
  }
  public static getEndOfWeek(date: Date = new Date()): Date {
    return moment(date).endOf("week").toDate();
  }
  public static getStartWeek(): Date {
    return moment().startOf("week").toDate();
  }

  public static numberToCurrency(value: any): string {
    if (typeof value == "undefined" || value == null) {
      return "";
    }
    var formatter = new Intl.NumberFormat("en-AU", {
      maximumFractionDigits: 40,
    });

    return formatter.format(parseFloat(value));
  }

  public static truncate(text: any, length: Number) {
    if (text && text.length > length) {
      return text.substring(0, length) + "...";
    } else {
      return text;
    }
  }
  public static moneyFormat(value: any, digits: number = 3): string {
    if (typeof value == "undefined" || value == null) {
      return "";
    }

    const newNumber = Utils.toDigitsDecimals(value, digits);
    return new Intl.NumberFormat("en-AU").format(parseFloat(newNumber));
  }

  public static accountingMoneyFormat(value: any, digits: number = 2, sign: string = "$"): string {
    let formatted = this.moneyFormat(Math.abs(value), digits);
    if (value < 0) {
      formatted = `(${formatted})`;
    } else {
    }
    return `${sign}${formatted}`;
  }

  public static momentAddBusinessDays(originalDate: any, numDaysToAdd: any) {
    let newDate = originalDate.clone();
    for (let i = 0; i < numDaysToAdd; i++) {
      if (newDate.isoWeekday() !== 6 && newDate.isoWeekday() !== 7) {
        newDate = newDate.add(1, "days");
      } else {
        newDate = newDate.add(1, "days");
        i--;
      }
    }
    return newDate;
  }

  public static toDigitsDecimals(num: number, digits: number = 3) {
    let str = num.toString();
    let decimalIndex = str.indexOf(".");
    if (decimalIndex === -1) {
      return str;
    }
    let intPart = str.slice(0, decimalIndex);
    let decPart = str.slice(decimalIndex + 1);
    if (decPart.length < digits) {
      while (decPart.length < digits) {
        decPart += "0";
      }
      return intPart + "." + decPart;
    }
    return intPart + "." + decPart.slice(0, digits);
  }

  public static currencyToNumber(value: string): number {
    if (value) {
      const retVal = parseFloat(value.replace(/[^0-9.-]+/g, ""));
      if (isNaN(retVal)) {
        return 0;
      } else {
        return retVal;
      }
    }
    return 0;
  }

  public static onlyUnique(value: any, index: any, self: any) {
    return self.indexOf(value) === index;
  }

  public static timestampToDate(tms: any): Date {
    if (tms instanceof Date) {
      return tms;
    } else {
      return new Date((tms._seconds || tms.seconds) * 1000);
    }
  }

  public static getActivityIcon(activity: string): string {
    let retVal = "mdi-help";
    if (!activity) {
      return retVal;
    }
    switch (activity.toLowerCase()) {
      case "research":
        retVal = "mdi-search-web";
        break;
      case "design":
        retVal = "mdi-brush";
        break;
      case "code review":
      case "code-review":
        retVal = "mdi-file-find";
        break;
      case "refactoring":
        retVal = "mdi-circle-edit-outline";
        break;
      case "meeting":
        retVal = "mdi-phone";
        break;
      case "testing the code":
      case "testing-the-code":
        retVal = "mdi-replay";
        break;
      case "coding":
        retVal = "mdi-code-braces";
        break;
      case "writing-unit-tests":
      case "writing unit tests":
        retVal = "mdi-test-tube";
        break;
    }
    return retVal;
  }

  public static addTimezoneOffset(date: any): Date {
    if (typeof date === "string") {
      date = this.vsDateToDatetime(date);
    }
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - userTimezoneOffset);
  }

  public static addTimezoneOffsetToStartDate(date: Date | null): Date | null {
    if (typeof date === "string") {
      date = this.vsDateToDatetime(date);
    }
    if (!date) {
      return date;
    }
    date = this.setDateTimes(date, 0, 0, 0, 0);
    return this.addTimezoneOffset(date);
  }
  public static setDateTimes(date: Date | null, hour: number, minute: number, second: number, millisecond: number) {
    if (date != null) {
      date.setHours(hour);
      date.setMinutes(minute);
      date.setSeconds(second);
      date.setMilliseconds(millisecond);
    }
    return date;
  }

  public static addTimezoneOffsetToEndDate(date: Date | null): Date | null {
    if (typeof date === "string") {
      date = this.vsDateToDatetime(date);
    }
    if (!date) {
      return date;
    }
    date = this.setDateTimes(date, 23, 59, 59, 999);
    return this.addTimezoneOffset(date);
  }

  public static removeTimezoneOffset(date: any) {
    let toDate;
    if (date.seconds || date._seconds) {
      toDate = this.timestampToDate(date);
    } else {
      toDate = date;
    }
    const dateOfsset = toDate.getTimezoneOffset();
    toDate.setMinutes(toDate.getMinutes() + dateOfsset);
    if (date.seconds) {
      date.seconds = toDate.getTime() / 1000;
    } else if (date._seconds) {
      date._seconds = toDate.getTime() / 1000;
    } else {
      date = toDate;
    }
    return date;
  }
  public static getYear(date: any) {
    date = this.vsDateToDatetime(date);
    return date.getFullYear();
  }

  public static getWeekNumber(date: any) {
    date = this.vsDateToDatetime(date);
    return moment(date).week();
  }

  public static toVsDateFormat(date: Date | string, disableDay = false): string {
    try {
      if (typeof date === "undefined" || date == null) {
        return "";
      }
      if (typeof date === "string") {
        date = this.vsDateToDatetime(date);
      }
      if (this.isTimstamp(date)) {
        date = this.timestampToDate(date);
      }

      const y = date.getFullYear().toString();
      let m = (date.getMonth() + 1).toString();
      let d = date.getDate().toString();

      if (m.length < 2) {
        m = "0" + m;
      }
      if (d.length < 2) {
        d = "0" + d;
      }
      if (disableDay) {
        return [y, m].join("-");
      } else {
        return [y, m, d].join("-");
      }
    } catch (error) {
      return "";
    }
  }

  public static vsDateToDatetime(date: string): Date {
    if (typeof date === "string") {
      const parts = date.split("-").map((p) => parseInt(p));
      if (AppHelper.IsAzureEnv()) {
        return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0));
      } else {
        return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
      }
    } else {
      const dateTimeType = date as Date;
      return dateTimeType;
    }
  }

  public static stringUtcToLocalDate(date: string | Date): Date {
    return moment.utc(moment(date).format("YYYY-MM-DD HH:mm")).local().toDate();
  }

  public static stringToDate(date: string | Date): Date {
    return moment(date).toDate();
  }

  public static getDateString(date: Date): string {
    return moment(date).format("YYYY-MM-DD");
  }

  public static lastOfTime(date: Date): Date {
    let newDate = new Date();
    if (date) {
      newDate = new Date(date);
      newDate.setHours(23);
      newDate.setMinutes(59);
      newDate.setSeconds(59);
    }

    return newDate;
  }

  public static firstDayOfMonth(date: Date): Date {
    let newDate = new Date();
    if (date) {
      newDate = new Date(date);
      newDate.setMonth(date.getMonth(), 1);
    }
    return newDate;
  }
  public static lastDayOfMonth(date: Date): Date {
    let newDate = new Date();
    if (date) {
      newDate = new Date(date);
      newDate.setMonth(date.getMonth() + 1, 0);
    }
    return newDate;
  }

  public static addMonths(date: Date, months: number): Date {
    date.setMonth(date.getMonth() + months);

    return date;
  }

  public static startOfWeek(): Date {
    return moment().startOf("week").toDate();
  }

  public static getMonthName(date: Date | number): string {
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    if (typeof date === "number") {
      return monthNames[date];
    } else {
      return monthNames[date.getMonth()];
    }
  }

  public static getCurrentUtcDate(): Date {
    const dt = new Date();
    return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), dt.getUTCHours(), dt.getUTCMinutes()));
  }
  private static isTimstamp(date: any) {
    return date.seconds ? true : false;
  }

  public static validateUrlRegex(value: any): boolean {
    return /^(https:\/\/)?([a-zA-Z0-9\-\_\.](?!.*\.\.)(?!.*\_\_)(?!.*\-\-))+[@]?[a-zA-Z0-9\-\_\.]+(?!.*\.\.)(?!.*\_\_)(?!.*\-\-)+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?((\/[a-zA-Z0-9\-\_\.]+(?!.*\.\.)(?!.*\_\_)(?!.*\-\-)){0,})?$/gi.test(
      value
    );
  }
  public static getRemainingDateByTimeDifference(timeDifference: number) {
    const millisecondsInOneSecond = 1000;
    const millisecondsInOneMinute = millisecondsInOneSecond * 60;
    const millisecondsInOneHour = millisecondsInOneMinute * 60;
    const millisecondsInOneDay = millisecondsInOneHour * 24;
    const differenceInDays = timeDifference / millisecondsInOneDay;
    const remainderDifferenceInHours = (timeDifference % millisecondsInOneDay) / millisecondsInOneHour;
    const remainderDifferenceInMinutes = (timeDifference % millisecondsInOneHour) / millisecondsInOneMinute;
    const remainderDifferenceInSeconds = (timeDifference % millisecondsInOneMinute) / millisecondsInOneSecond;
    const remainingMinutes = Math.floor(remainderDifferenceInMinutes);
    const remainingSeconds = Math.floor(remainderDifferenceInSeconds);
    const remainingDays = Math.floor(differenceInDays);
    const remainingHours = Math.floor(remainderDifferenceInHours);
    var date = {
      Day: remainingDays,
      Hour: remainingHours,
      Minute: remainingMinutes,
      Second: remainingSeconds,
    } as RemainingDate;
    return date;
  }

  public static formatDate(dt: Date | string): string {
    if (dt instanceof Date) {
      return Utils.toVsDateFormat(dt);
    } else {
      if (dt) {
        return dt.replace(/\-/gi, "/");
      }
      return "";
    }
  }

  public static formatTime(seconds: number | null | undefined): string {
    if (!seconds) {
      return "";
    }

    // Calculate years
    const years = Math.floor(seconds / (365 * 24 * 3600));
    // Calculate remaining seconds after extracting years
    seconds %= 365 * 24 * 3600;
    // Calculate days
    const days = Math.floor(seconds / (24 * 3600));
    // Calculate remaining seconds after extracting days
    seconds %= 24 * 3600;
    // Calculate hours
    const hours = Math.floor(seconds / 3600);
    // Calculate remaining seconds after extracting hours
    seconds %= 3600;
    // Calculate minutes
    const minutes = Math.floor(seconds / 60);
    // Remaining seconds
    const remainingSeconds = seconds % 60;

    // Construct the formatted time string
    let formattedTime = "";
    if (years > 0) {
      formattedTime += years + "y ";
    }
    if (days > 0) {
      formattedTime += days + "d ";
    }
    if (hours > 0) {
      formattedTime += hours + "h ";
    }
    if (minutes > 0) {
      formattedTime += minutes + "m ";
    }
    if (remainingSeconds > 0 || formattedTime === "") {
      formattedTime += remainingSeconds.toFixed(0) + "s";
    }

    return formattedTime.trim();
  }

  public static getSplittedWeeks(fromDate: Date, toDate: Date): WeekRange[] {
    const fDate = fromDate;
    const tDate = moment(toDate).endOf("day").toDate();
    const retVal = [] as WeekRange[];
    if (fDate && tDate) {
      let dt = new Date(fDate);
      while (dt <= tDate) {
        const item: WeekRange = { from: this.formatDate(dt), to: "" } as WeekRange;
        dt = this.getEndOfWeek(dt);
        if (dt > tDate) {
          dt = new Date(tDate);
        }
        item.to = this.formatDate(dt);
        dt.setDate(dt.getDate() + 1);
        retVal.push(item);
      }
    }
    return retVal;
  }

  public static GetLimitedDateForTimesheet(isAdmin: any) {
    const limitedDays = isAdmin ? 365 : 6;
    const dateLimit = new Date();
    dateLimit.setDate(dateLimit.getDate() - limitedDays);
    dateLimit.setHours(0);
    dateLimit.setMinutes(0);
    dateLimit.setSeconds(0);
    dateLimit.setMilliseconds(0);
    return dateLimit;
  }
  public static getTimeAgo(previousDate: Date): string {
    const currentDate = new Date(); // Current date/time in UTC
    const timeDifference = currentDate.getTime() - previousDate.getTime();
    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return Utils.toVsDateFormat(previousDate); // Convert to local date/time string
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? "s" : ""} ago`; // Handle pluralization
    } else if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`; // Handle pluralization
    } else {
      return "Just now";
    }
  }
}
